/**
 * `EVADE.DAT` — the original's evasive-manoeuvre selection weights, transcribed
 * from the shipped file and read here to weight the AI's own evasion.
 *
 * **What the file actually is** (our own read of both copies, floppy and CD,
 * byte-identical at 291 bytes — refines the PARTIAL entry in
 * docs/formats/missions-campaign.md, which had the header right and the body
 * split unresolved):
 *
 * ```
 * byte[0..9]   ten SDEF.DAT manoeuvre ids: 10, 1, 2, 3, 0, 12, 10, 4, 5, 11
 * byte[10]     228
 * byte[11..]   280 bytes = 28 rows x 10 columns of selection weights
 * ```
 *
 * `11 + 28·10 = 291` consumes the file exactly, and the 28 rows are only 8
 * distinct vectors (`EVADE_ROWS` below), repeated in the `EVADE_ROW_SEQUENCE`
 * order. `28 = 4 × 7` and the sequence falls into four clean blocks of seven,
 * which is why this port reads the row axis as **four pilot-skill tiers × seven
 * situations** — the same four tiers the Mission Builder's pilot picker offers.
 * That reading is **INFERRED**: the split is arithmetically forced, the meaning
 * of each axis is not, and the seven situation labels below are entirely
 * port-side. `byte[10] = 228` is still **UNKNOWN** — it is not a body length
 * (the body is 280 bytes) and not a row/column count.
 *
 * The id→manoeuvre mapping is likewise **INFERRED**, read off the `SDEF.DAT`
 * parameter block (10 records, `int16[7]` each, our own parse): the fourth and
 * fifth parameters are a bounded ±pair that is left-only on ids 1 and 4,
 * right-only on 2 and 5, and symmetric elsewhere, and the seventh is a
 * magnitude that orders the records 500 (id 10) … 25 000 (id 3). So the
 * direction-biased pairs are turns, the largest magnitude is the hardest turn
 * available, the smallest is "hold what you have", and the one record whose
 * *vertical* bound reaches −12 000 (−65.9°) with a large magnitude is the dive.
 * Id 6 has no column in `EVADE.DAT` at all, and id 10 has two — the weights of
 * both of its columns are summed.
 */

import type { RngBank } from "../flight/rng.ts";
import type { ManeuverCommand, ManeuverId } from "./maneuvers.ts";
import { canFly, type SkillProfile } from "./skill.ts";

/** `EVADE.DAT` bytes 0–9: the manoeuvre id each weight column selects. */
export const EVADE_COLUMN_IDS: readonly number[] = [10, 1, 2, 3, 0, 12, 10, 4, 5, 11];

/** `EVADE.DAT` byte 10. Not a length, not a count — UNKNOWN. */
export const EVADE_HEADER_WORD = 228;

/** The eight distinct weight rows the 280-byte body is built from. */
export const EVADE_ROWS: readonly (readonly number[])[] = [
  [0, 0, 0, 0, 20, 10, 0, 0, 0, 10],
  [0, 0, 0, 0, 20, 10, 5, 5, 0, 10],
  [10, 10, 10, 40, 20, 5, 8, 8, 10, 20],
  [0, 0, 0, 0, 20, 0, 10, 10, 0, 10],
  [10, 10, 10, 0, 20, 0, 10, 10, 10, 10],
  [30, 30, 10, 90, 0, 0, 0, 0, 30, 50],
  [30, 30, 10, 50, 20, 0, 10, 10, 20, 40],
  [30, 30, 0, 50, 10, 0, 10, 10, 0, 30],
];

/** The 28 body rows in file order, as indices into `EVADE_ROWS`. */
export const EVADE_ROW_SEQUENCE: readonly number[] = [
  0, 1, 2, 3, 4, 5, 6, 4, 3, 6, 4, 5, 6, 6, 4, 7, 4, 5, 2, 7, 6, 4, 1, 6, 7, 7, 5, 4,
];

/** Rows per block under the 4×7 reading. */
export const EVADE_SITUATIONS_PER_BLOCK = 7;

/**
 * The seven situations this port maps the within-block row axis onto. Entirely
 * port-side naming — the file gives no labels — but the ordering is chosen so
 * "worse trouble" reads down the block, which is how the weights themselves
 * trend (the last rows of every block carry the heaviest hard-turn weights).
 */
export const EVADE_SITUATIONS = [
  "clear",
  "threatDistant",
  "threatAstern",
  "threatGuns",
  "underFire",
  "damaged",
  "lowAltitude",
] as const;

export type EvadeSituation = (typeof EVADE_SITUATIONS)[number];

/** `SDEF.DAT` manoeuvre id → this port's manoeuvre, with the direction the bounds imply. */
export const SDEF_MANEUVER_MAP: Readonly<Record<number, ManeuverCommand>> = {
  0: { id: "dive", direction: 1 },
  1: { id: "breakTurn", direction: -1 },
  2: { id: "breakTurn", direction: 1 },
  3: { id: "breakTurn", direction: 0 },
  4: { id: "slipTurn", direction: -1 },
  5: { id: "slipTurn", direction: 1 },
  6: { id: "sideslip", direction: 1 },
  10: { id: "extend", direction: 0 },
  11: { id: "splitS", direction: 1 },
  12: { id: "barrelRoll", direction: 1 },
};

export function evadeRowIndex(skillBlock: number, situation: EvadeSituation): number {
  const block = Math.min(Math.max(Math.trunc(skillBlock), 0), 3);
  const offset = Math.max(0, EVADE_SITUATIONS.indexOf(situation));
  return block * EVADE_SITUATIONS_PER_BLOCK + offset;
}

/** One 10-column weight row, straight out of the file. */
export function evadeWeights(skillBlock: number, situation: EvadeSituation): readonly number[] {
  const rowIndex = EVADE_ROW_SEQUENCE[evadeRowIndex(skillBlock, situation)] ?? 0;
  return EVADE_ROWS[rowIndex] ?? EVADE_ROWS[0] ?? [];
}

/**
 * Picks an evasive manoeuvre. Columns whose manoeuvre this pilot cannot fly are
 * zeroed rather than remapped, so a novice's evasion really is a narrower set
 * — and the two columns that share id 10 both contribute, which is the honest
 * reading of a duplicated column.
 */
export function pickEvasion(
  profile: SkillProfile,
  situation: EvadeSituation,
  preferredDirection: number,
  rng: RngBank,
): ManeuverCommand {
  const weights = evadeWeights(profile.evadeBlock, situation);
  const allowed = EVADE_COLUMN_IDS.map((id, column) => {
    const command = SDEF_MANEUVER_MAP[id];
    if (!command || !canFly(profile, command.id)) return 0;
    return weights[column] ?? 0;
  });
  const chosen = rng.weightedIndex("ai", allowed);
  const fallback: ManeuverCommand = { id: "breakTurn", direction: preferredDirection };
  if (chosen < 0) return fallback;
  const command = SDEF_MANEUVER_MAP[EVADE_COLUMN_IDS[chosen] ?? -1];
  if (!command) return fallback;
  return command.direction === 0 ? { id: command.id, direction: preferredDirection } : command;
}

/** Manoeuvres reachable through the evade table at all, for the coverage test. */
export function evadeManeuverIds(): readonly ManeuverId[] {
  const ids = EVADE_COLUMN_IDS.map((id) => SDEF_MANEUVER_MAP[id]?.id).filter(
    (id): id is ManeuverId => id !== undefined,
  );
  return [...new Set(ids)];
}
